import api from 'common/api';
import type { TranscriptHighlight, NoteTag, NoteNotificationPayload, LineSelection } from './types';

export type { NoteTag };

// --- Request/Response Types ---

export type NoteSelection = {
  type: 'FullLine' | 'WordRange';
  start?: number;
  end?: number;
  selectedText?: string;
};

export type CaseTag = {
  id: string;
  code: string;
  name: string;
  createdBy: string;
};

export type CreateNoteRequest = {
  selections: Array<{
    lineId: string;
    lineNumberAtCreate?: number;
    pageNumber?: number;
    type: 'WordRange' | 'FullLine';
    start?: number;
    end?: number;
    selectedText?: string;
  }>;
  tags?: NoteTag[];
  noteText?: string;
  visibility?: string;
  teamId?: string;
};

export type UpdateNoteRequest = {
  noteText?: string;
  tags?: NoteTag[];
  visibility?: string;
  teamId?: string;
};

export type NoteModel = {
  id: string;
  lineId?: string;
  lineIds?: string[];
  lineNumberAtCreate?: number;
  selection?: NoteSelection;
  selections?: Array<{
    lineId: string;
    lineNumberAtCreate?: number;
    pageNumber?: number;
    type: 'WordRange' | 'FullLine';
    start?: number;
    end?: number;
    selectedText?: string;
  }>;
  tags?: NoteTag[];
  noteText?: string;
  visibility?: string;
  teamId?: string;
  createdAt?: string;
  createdAtUtc?: string;
  updatedAt?: string;
  updatedAtUtc?: string;
  createdBy?: { id: string; name: string; teamId?: string; teamName?: string };
  hasComments?: boolean;
  comments?: Array<{
    commentId: string;
    commentText: string;
    createdAtUtc: string;
    createdBy: { id: string; name: string; teamId?: string; teamName?: string };
  }>;
};

// --- API Functions ---

const notesBasePath = (caseId: string, sessionKey: string) =>
  `/triggers/cases/${encodeURIComponent(caseId)}/realtime-transcripts/${encodeURIComponent(sessionKey)}/notes`;

export async function createNote(caseId: string, sessionKey: string, body: CreateNoteRequest) {
  return api.post(notesBasePath(caseId, sessionKey), body) as Promise<NoteModel>;
}

export async function fetchNotes(
  caseId: string,
  sessionKey: string,
  params?: { lineIds?: string; tagIds?: string; take?: number },
) {
  const query = new URLSearchParams();
  if (params?.lineIds) query.set('lineIds', params.lineIds);
  if (params?.tagIds) query.set('tagIds', params.tagIds);
  if (params?.take) query.set('take', String(params.take));
  const qs = query.toString();
  const url = `${notesBasePath(caseId, sessionKey)}${qs ? `?${qs}` : ''}`;
  return api.get(url) as Promise<NoteModel[]>;
}

export async function updateNote(
  caseId: string,
  sessionKey: string,
  noteId: string,
  body: UpdateNoteRequest,
) {
  return api.patch(
    `${notesBasePath(caseId, sessionKey)}/${encodeURIComponent(noteId)}`,
    body,
  ) as Promise<NoteModel>;
}

export async function deleteNote(caseId: string, sessionKey: string, noteId: string) {
  return api.del(
    `${notesBasePath(caseId, sessionKey)}/${encodeURIComponent(noteId)}`,
  ) as Promise<void>;
}

export type CommentResponse = {
  id: string;
  caseId: string;
  sessionKey: string;
  noteId: string;
  commentText: string;
  createdBy: { id: string; name: string; teamId?: string; teamName?: string };
  createdAtUtc: string;
  isDeleted: boolean;
};

export async function createComment(
  caseId: string,
  sessionKey: string,
  noteId: string,
  commentText: string,
): Promise<CommentResponse> {
  return api.post(`${notesBasePath(caseId, sessionKey)}/${encodeURIComponent(noteId)}/comments`, {
    commentText,
  }) as Promise<CommentResponse>;
}

export async function fetchCaseTags(caseId: string) {
  return api.get(`/cases/${encodeURIComponent(caseId)}/tags`) as Promise<CaseTag[]>;
}

export async function createCaseTag(caseId: string, name: string) {
  return api.post(`/cases/${encodeURIComponent(caseId)}/tags`, { name }) as Promise<CaseTag>;
}

// --- Mapping Utilities ---

export function highlightToCreateRequest(hl: TranscriptHighlight): CreateNoteRequest {
  const lineSelections: LineSelection[] =
    hl.selections && hl.selections.length > 0
      ? hl.selections
      : [
          {
            lineId: hl.lineId,
            lineNumberAtCreate: hl.lineNumberAtCreate,
            pageNumber: hl.pageNumber,
            startOffset: hl.startOffset,
            endOffset: hl.endOffset,
            text: hl.text,
          },
        ];

  return {
    selections: lineSelections.map((sel) => ({
      lineId: sel.lineId,
      lineNumberAtCreate: sel.lineNumberAtCreate,
      pageNumber: sel.pageNumber,
      type: 'WordRange' as const,
      start: sel.startOffset,
      end: sel.endOffset,
      selectedText: sel.text,
    })),
    tags: hl.tags && hl.tags.length > 0 ? hl.tags : undefined,
    noteText: hl.note || undefined,
    visibility: hl.visibility || 'private',
  };
}

export function noteNotificationToHighlight(payload: NoteNotificationPayload): TranscriptHighlight {
  // Support both PascalCase (legacy NoteCreated) and camelCase (new NoteUpdated format)
  const noteId = payload.NoteId ?? payload.noteId ?? '';
  const noteText = payload.NoteText ?? payload.noteText ?? '';
  const visibility = payload.Visibility ?? payload.visibility;
  const createdByUserId = payload.CreatedByUserId ?? payload.createdByUserId;
  const createdByName = payload.CreatedByUsername ?? payload.createdByUsername;

  const rawTags = payload.Tags ?? payload.tags;
  const tags =
    rawTags && rawTags.length > 0
      ? rawTags.map((t: { Id?: string; Name?: string; id?: string; name?: string }) => ({
          id: t.Id ?? t.id ?? '',
          name: t.Name ?? t.name ?? '',
        }))
      : undefined;

  let selections: LineSelection[] | undefined;
  let lineId = payload.LineId ?? payload.LineIds?.[0] ?? payload.lineIds?.[0] ?? '';
  let lineNumberAtCreate = payload.LineNumberAtCreate;
  let startOffset = payload.Selection?.Start ?? 0;
  let endOffset = payload.Selection?.End ?? 0;
  let text = payload.Selection?.SelectedText ?? '';

  if (payload.Selections && payload.Selections.length > 0) {
    selections = payload.Selections.map((s) => ({
      lineId: s.LineId,
      lineNumberAtCreate: s.LineNumberAtCreate,
      pageNumber: s.PageNumber,
      startOffset: s.Start,
      endOffset: s.End,
      text: s.SelectedText ?? '',
    }));
    const first = selections[0];
    lineId = first.lineId;
    lineNumberAtCreate = first.lineNumberAtCreate;
    startOffset = first.startOffset;
    endOffset = first.endOffset;
    text = selections.map((s) => s.text).join(' ');
    if (selections.length === 1) selections = undefined;
  } else if (payload.selections && payload.selections.length > 0) {
    const wordRanges = payload.selections.filter((s) => s.type !== 'Line');
    if (wordRanges.length > 0) {
      selections = wordRanges.map((s) => ({
        lineId: s.lineId,
        lineNumberAtCreate: s.lineNumberAtCreate,
        pageNumber: s.pageNumber,
        startOffset: s.start,
        endOffset: s.end,
        text: s.selectedText ?? '',
      }));
      const first = selections[0];
      lineId = first.lineId;
      lineNumberAtCreate = first.lineNumberAtCreate;
      startOffset = first.startOffset;
      endOffset = first.endOffset;
      text = selections.map((s) => s.text).join(' ');
      if (selections.length === 1) selections = undefined;
    }
  }

  return {
    highlightId: `note-${noteId}`,
    noteId,
    lineId,
    lineNumberAtCreate,
    startOffset,
    endOffset,
    text,
    note: noteText,
    tags,
    visibility: (visibility === 'team' ? 'team' : 'private') as 'private' | 'team',
    createdAt: payload.CreatedAtUtc ? new Date(payload.CreatedAtUtc).getTime() : Date.now(),
    createdByUserId,
    createdByName,
    isCommentsVisible: true,
    selections,
  };
}

export function noteModelToHighlight(note: NoteModel): TranscriptHighlight {
  let selections: LineSelection[] | undefined;
  let lineId = note.lineId ?? note.lineIds?.[0] ?? '';
  let lineNumberAtCreate = note.lineNumberAtCreate;
  let startOffset = note.selection?.start ?? 0;
  let endOffset = note.selection?.end ?? 0;
  let text = note.selection?.selectedText ?? '';

  if (note.selections && note.selections.length > 0) {
    selections = note.selections.map((s) => ({
      lineId: s.lineId,
      lineNumberAtCreate: s.lineNumberAtCreate,
      pageNumber: s.pageNumber,
      startOffset: s.start ?? 0,
      endOffset: s.end ?? 0,
      text: s.selectedText ?? '',
    }));
    const first = selections[0];
    lineId = first.lineId;
    lineNumberAtCreate = first.lineNumberAtCreate;
    startOffset = first.startOffset;
    endOffset = first.endOffset;
    text = selections.map((s) => s.text).join(' ');
    if (selections.length === 1) selections = undefined;
  }

  return {
    highlightId: `note-${note.id}`,
    noteId: note.id,
    lineId,
    lineNumberAtCreate,
    startOffset,
    endOffset,
    text,
    note: note.noteText ?? '',
    tags: note.tags && note.tags.length > 0 ? note.tags : undefined,
    visibility: (note.visibility === 'team' ? 'team' : 'private') as 'private' | 'team',
    createdAt:
      note.createdAtUtc || note.createdAt
        ? new Date(note.createdAtUtc || note.createdAt!).getTime()
        : Date.now(),
    createdByUserId: note.createdBy?.id,
    createdByName: note.createdBy?.name,
    isCommentsVisible: true,
    selections,
    hasComments: note.hasComments ?? (note.comments?.length ?? 0) > 0,
    comments: note.comments ?? [],
  };
}
